import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import { CopyHash } from '../../features/web3/forex/components/CopyHash';
import { bscAddressUrl } from '../../lib/format';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/dropdown-menu';

const BFMWithdrawals = () => {
  const { token } = useSelector((state) => state.auth);
  const { can } = usePermissions();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 30,
    startDate: '',
    endDate: '',
    username: ''
  });
  // Search only fires on Enter/Apply — a text input wired straight to filters
  // would hit the API on every keystroke (this page has no tempFilters).
  const [searchInput, setSearchInput] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRows: 0
  });
  const [loadingAction, setLoadingAction] = useState(false);
  const [showApproveConfirmModal, setShowApproveConfirmModal] = useState(false);
  const [showRejectConfirmModal, setShowRejectConfirmModal] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);

  const fetchWithdrawals = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.page) params.page = filters.page;
      if (filters.pageSize) params.pageSize = filters.pageSize;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.username) params.username = filters.username;

      const response = await axiosInstance.get('/api/admin/withdrawls/bfm/history', {
        headers: { token },
        params
      });

      if (response.data.success) {
        setWithdrawals(response.data.data.data);
        setPagination({
          currentPage: parseInt(response.data.data.currentPage) || 1,
          totalPages: response.data.data.totalPages || 1,
          totalRows: response.data.data.totalRows || 0
        });
      }
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      toast.error('Failed to fetch withdrawals');
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1
    }));
  };

  const handlePageChange = (newPage) => {
    handleFilterChange('page', newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    handleFilterChange('pageSize', newPageSize);
    handleFilterChange('page', 1);
  };

  const handleApprove = async (withdrawId) => {
    const withdrawal = withdrawals.find(w => w.id === withdrawId);
    setSelectedWithdrawal(withdrawal);
    setShowApproveConfirmModal(true);
  };

  const confirmApprove = async () => {
    try {
      setLoadingAction(true);
      const response = await axiosInstance.post('/api/admin/withdrawls/bfm/approve', {
        withdrawId: selectedWithdrawal.id
      }, {
        headers: { token }
      });
      if (response.data.success) {
        toast.success('Withdrawal approved successfully');
        setShowApproveConfirmModal(false);
        fetchWithdrawals();
      }
    } catch (error) {
      console.error('Error approving withdrawal:', error);
      toast.error('Failed to approve withdrawal');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleReject = async (withdrawId) => {
    const withdrawal = withdrawals.find(w => w.id === withdrawId);
    setSelectedWithdrawal(withdrawal);
    setShowRejectConfirmModal(true);
  };

  const confirmReject = async () => {
    try {
      setLoadingAction(true);
      const response = await axiosInstance.post('/api/admin/withdrawls/bfm/reject', {
        withdrawId: selectedWithdrawal.id
      }, {
        headers: { token }
      });
      if (response.data.success) {
        toast.success('Withdrawal rejected successfully');
        setShowRejectConfirmModal(false);
        fetchWithdrawals();
      }
    } catch (error) {
      console.error('Error rejecting withdrawal:', error);
      toast.error('Failed to reject withdrawal');
    } finally {
      setLoadingAction(false);
    }
  };

  const applySearch = () => handleFilterChange('username', searchInput);

  const clearFilters = () => {
    setSearchInput('');
    setFilters({ page: 1, pageSize: 30, startDate: '', endDate: '', username: '' });
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">Pending</span>;
      case 2:
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/10 text-red-400 border border-red-500/20">Rejected</span>;
      case 3:
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Approved</span>;
      default:
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-white/[0.05] text-muted-foreground border border-border/40">Unknown</span>;
    }
  };

  const ActionDropdown = ({ withdrawal }) => (
    <div className="inline-block text-left">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            aria-label="Withdrawal actions"
            type="button"
            className="group inline-flex items-center gap-1 rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-foreground hover:bg-white/[0.06] transition-colors"
          >
            Actions
            <ChevronDown className="h-3 w-3 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-44">
          {withdrawal.status === 1 && (
            <>
              {can('POST:/api/admin/withdrawls/approve') && (
                <DropdownMenuItem
                  onSelect={() => handleApprove(withdrawal._id)}
                  disabled={loadingAction}
                  className="px-4 py-2 text-xs text-emerald-400 focus:bg-emerald-500/10 focus:text-emerald-400"
                >
                  Approve
                </DropdownMenuItem>
              )}
              {can('POST:/api/admin/withdrawls/reject') && (
                <DropdownMenuItem
                  onSelect={() => handleReject(withdrawal._id)}
                  disabled={loadingAction}
                  className="px-4 py-2 text-xs text-red-400 focus:bg-red-500/10 focus:text-red-400"
                >
                  Reject
                </DropdownMenuItem>
              )}
            </>
          )}
          {withdrawal.status !== 1 && (
            <div className="px-4 py-2 text-xs text-muted-foreground">
              No actions available
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const inputCls = "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
  const labelCls = "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Finance
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        BFM <span className="text-gradient-ginox">Withdrawals</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        View and manage BFM withdrawal requests
      </p>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className={labelCls}>Username / Trx ID / WalletAddress / Payout Address</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
              placeholder="Search…"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select
              value={filters.pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              className={inputCls}
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page</label>
            <select
              value={filters.page}
              onChange={(e) => handlePageChange(parseInt(e.target.value))}
              className={inputCls}
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={applySearch}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <table className="w-full border-separate border-spacing-0">
            <tbody>
              <tr>
                <td colSpan={12} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <>
            <table className="w-full border-separate border-spacing-0" style={{ minWidth: '1200px' }}>
              <thead>
                <tr>
                  {['Username', 'Amount', 'Transaction ID', 'Wallet Type', 'Fee', 'Payable', 'Method', 'Address', 'Status', 'Created At', 'Updated At', 'Actions'].map((h) => (
                    <th key={h} className="border-b border-border/70 px-5 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac]">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((withdrawal) => (
                  <tr key={withdrawal._id} className="group hover:bg-white/[0.02] transition-colors">
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      <div>{withdrawal.username}</div>
                      <div className="text-xs text-muted-foreground/80">
                        <CopyHash value={withdrawal.walletAddress} href={bscAddressUrl(withdrawal.walletAddress)} head={6} tail={4} />
                      </div>
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{withdrawal.amount}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{withdrawal.tranx_id}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{withdrawal.wallet_type}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{withdrawal.fee}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{withdrawal.payable}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{withdrawal.method}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                      <CopyHash value={withdrawal.address} href={bscAddressUrl(withdrawal.address)} head={6} tail={4} />
                    </td>
                    <td className="border-b border-border/40 px-5 py-3.5">{getStatusBadge(withdrawal.status)}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono whitespace-nowrap">{formatDate(withdrawal.createdAt)}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono whitespace-nowrap">{formatDate(withdrawal.updatedAt)}</td>
                    <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                      <ActionDropdown withdrawal={withdrawal} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

          </>
        )}

        <ConfirmationModal
          isOpen={showApproveConfirmModal}
          onClose={() => setShowApproveConfirmModal(false)}
          onConfirm={confirmApprove}
          title="Approve Withdrawal"
          message={`Are you sure you want to approve the withdrawal of ${selectedWithdrawal?.amount} BFM for user ${selectedWithdrawal?.username}?`}
          confirmText="Approve"
          loading={loadingAction}
          loadingText="Approving..."
        />
        <ConfirmationModal
          isOpen={showRejectConfirmModal}
          onClose={() => setShowRejectConfirmModal(false)}
          onConfirm={confirmReject}
          title="Reject Withdrawal"
          message={`Are you sure you want to reject the withdrawal of ${selectedWithdrawal?.amount} BFM for user ${selectedWithdrawal?.username}?`}
          confirmText="Reject"
          loading={loadingAction}
          loadingText="Rejecting..."
          danger={true}
        />
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default BFMWithdrawals;
